import React, { useEffect, useState } from 'react';

import { Button, Group, Modal, Stack, Text, Title } from '@mantine/core';
import KiloAlert from 'components/KiloAlert';
import usePaymentInfoRequestCandidates, {
  PaymentInfoRequestFilters,
} from 'hooks/athlete/usePaymentInfoRequestCandidates';
import useRequestPaymentInfo from 'hooks/athlete/useRequestPaymentInfo';
import notificationHandler from 'utils/notifications';

import AthleteCandidateTable from './AthleteCandidateTable';

interface ISendPaymentInfoRequestModalProps {
  opened: boolean;
  onClose: () => void;
  filters: PaymentInfoRequestFilters;
}

const SendPaymentInfoRequestModal = ({
  opened,
  onClose,
  filters,
}: ISendPaymentInfoRequestModalProps): JSX.Element => {
  const { successNotification, errorNotification } = notificationHandler();

  const { data: candidates, isLoading } = usePaymentInfoRequestCandidates(filters, { enabled: opened });
  const requestPaymentInfo = useRequestPaymentInfo();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (opened && candidates) {
      setSelectedIds(new Set(candidates.filter(c => c.preselected).map(c => c.athleteId)));
    }
  }, [opened, candidates]);

  const candidateList = candidates ?? [];

  const toggle = (athleteId: string): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(athleteId)) {
        next.delete(athleteId);
      } else {
        next.add(athleteId);
      }
      return next;
    });
  };

  const handleSend = (): void => {
    const ids = [...selectedIds];
    requestPaymentInfo.mutate(ids, {
      onSuccess: result => {
        const count = result.sentCount;
        successNotification(
          `Payment request sent to ${count} ${count === 1 ? 'athlete' : 'athletes'}`,
          // Bulk send fans out across many athletes, so there is no single
          // Communications view to link to — kept as plain text (DEV-2727 Q4).
          "You can track this in each athlete's Communications tab.",
        );
        onClose();
      },
      onError: error => {
        errorNotification('Payment request failed to send', error.response?.data?.message);
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Title order={4}>Send Payment Info Request</Title>}
      size='lg'
      centered
      data-cy='sendPaymentInfoRequestModal'
    >
      <Stack spacing='md'>
        <KiloAlert>
          An email will be sent to the selected athletes asking them to add a payment method. Uncheck anyone
          who shouldn&apos;t receive this request (e.g. athletes who pay with cash).
        </KiloAlert>

        <AthleteCandidateTable
          candidates={candidateList}
          selectedIds={selectedIds}
          onToggle={toggle}
          isLoading={isLoading}
          checkboxDataCy='paymentInfoRequestCandidateCheckbox'
        />

        <Text size='xs' color='dimmed'>
          Only members are selected by default. Prospects and alumni are unchecked.
        </Text>

        <Group position='apart'>
          <Text size='sm' data-cy='paymentInfoRequestSelectedCount'>
            {selectedIds.size} of {candidateList.length} athletes selected
          </Text>
          <Group spacing='sm'>
            <Button variant='subtle' color='gray' onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              loading={requestPaymentInfo.isLoading}
              disabled={selectedIds.size === 0}
              data-cy='sendPaymentInfoRequestBtn'
            >
              Send request
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SendPaymentInfoRequestModal;
